import { Box, Drawer, Typography, Button, CircularProgress, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import { snowUrl } from 'service/config';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { ReactComponent as GenerateRcaIcon } from 'assets/generate_rca.svg';
import RcaStatusModal from './RcaStatusModal';
import MarkdownRenderer from './MarkdownRenderer';
import { generateRCA, getRcaStatus, getRcaReport } from 'service/unifiedAlerts';

const SNOW_TYPE_MAP: Record<string, string> = {
  "Problem Tickets": "problem",
  "Incident Tickets": "incident",
  "Incidents": "incident",
  "Change Requests": "change_request",
  "Service Requests": "sc_req_item",
};

const DetailedAlerts = ({
  open,
  onClose,
  onOpen,
  entireRow,
  header,
}: {
  open: boolean;
  onClose: () => void;
  onOpen?: () => void;
  entireRow: any;
  header?: any;
}) => {
  const theme = useTheme();
  const overlayColors = theme.palette.overlay;
  const inputColors = theme.palette.inputNew;
  const buttonColors = theme.palette.buttons.primary;
  const accordionColors = theme.palette.accordion;
  const scrollbarColors = theme.palette.scrollbar;
  const textColors = theme.palette.textLight;
  const tooltipColors = theme.palette.tooltip;

  const [generatingId, setGeneratingId] = useState<string | null>(null);
  const [rcaStatus, setRcaStatus] = useState<'success' | 'failed' | null>(null);
  // Separate state for the popup — set unconditionally when generation finishes,
  // even if the user has switched to a different incident in the meantime.
  const [modalStatus, setModalStatus] = useState<'success' | 'failed' | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [rcaExpanded, setRcaExpanded] = useState(false);
  const [rcaReportText, setRcaReportText] = useState<string>('');
  const isMounted = useRef(true);
  const activeIncidentRef = useRef<string | null>(null);
  // Tracks which incident has an active generate-RCA loop so it survives drawer switches.
  const generateLoopRef = useRef<string | null>(null);

  useEffect(() => {
    isMounted.current = true; // Fix for React 18 Strict Mode
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (!entireRow || header !== 'Incidents') {
      activeIncidentRef.current = null;
      setRcaStatus(null);
      setRcaExpanded(false);
      setRcaReportText('');
      setGeneratingId(null);
      return;
    }

    const incidentId = entireRow?.inc_sys_id || entireRow?.sys_id || entireRow?.alertId || entireRow?.incident || entireRow?.number || 'Unknown';
    if (incidentId === 'Unknown') return;

    activeIncidentRef.current = incidentId;

    // If the user switches back to an incident whose generate-loop is still running,
    // just restore the spinner — do NOT reset state or start a duplicate fetch-status poll.
    if (generateLoopRef.current === incidentId) {
      setGeneratingId(incidentId);
      return;
    }

    // Normal reset for a freshly opened incident
    setRcaStatus(null);
    setRcaExpanded(false);
    setRcaReportText('');
    setGeneratingId(null);

    const fetchStatus = async () => {
      try {
        let statusResp = await getRcaStatus(incidentId);
        let currentStatus = statusResp?.status;

        if (activeIncidentRef.current !== incidentId || !isMounted.current) return;

        if (currentStatus === 'triggered' || currentStatus === 'generating') {
          setGeneratingId(incidentId);
          while ((currentStatus === 'triggered' || currentStatus === 'generating') && isMounted.current && activeIncidentRef.current === incidentId) {
            await new Promise(r => setTimeout(r, 5000));
            if (!isMounted.current || activeIncidentRef.current !== incidentId) break;
            statusResp = await getRcaStatus(incidentId);
            currentStatus = statusResp?.status;
          }
          if (isMounted.current && activeIncidentRef.current === incidentId) {
            setGeneratingId(null);
          }
        }

        if (currentStatus === 'completed' && isMounted.current && activeIncidentRef.current === incidentId) {
          const reportResp = await getRcaReport(incidentId);
          if (isMounted.current && activeIncidentRef.current === incidentId) {
            setRcaReportText(reportResp?.narrative || reportResp?.failure_summary || 'RCA completed successfully.');
            setRcaStatus('success');
            setRcaExpanded(true);
          }
        }
      } catch (err) {
        console.error("Error fetching RCA status:", err);
      }
    };

    fetchStatus();
  }, [entireRow, header]);

  // Derive a stable incident ID from the row — avoids repeating the long OR chain.
  const currentIncidentId = entireRow?.inc_sys_id || entireRow?.sys_id || entireRow?.alertId || entireRow?.incident || entireRow?.number || 'Unknown';
  // Spinner is scoped to the specific incident being processed, not the whole list.
  const isGeneratingRCA = generatingId === currentIncidentId;

  const handleGenerateRCA = useCallback(async () => {
    if (generatingId !== null) return; // already generating something

    const incidentId = currentIncidentId;
    if (incidentId === 'Unknown') return;

    setGeneratingId(incidentId);
    generateLoopRef.current = incidentId; // mark this incident as actively generating
    setRcaReportText('');

    try {
      const response = await generateRCA(entireRow);
      let currentStatus = response?.status || 'generating';

      // Polling loop — only stops on unmount, NOT when user switches incidents.
      // This keeps the background generation alive across drawer navigation.
      while ((currentStatus === 'triggered' || currentStatus === 'generating') && isMounted.current) {
        await new Promise(r => setTimeout(r, 5000));
        if (!isMounted.current) return;
        const statusResp = await getRcaStatus(incidentId);
        currentStatus = statusResp?.status;
      }

      if (!isMounted.current) return;

      if (currentStatus === 'completed') {
        const reportResp = await getRcaReport(incidentId);
        if (!isMounted.current) return;
        // Update the accordion content only if the user is still on this incident.
        // The modal popup always fires to notify the user regardless.
        if (activeIncidentRef.current === incidentId) {
          setRcaReportText(reportResp?.narrative || reportResp?.failure_summary || 'RCA completed successfully.');
          setRcaStatus('success');
        }
        setModalStatus('success');
        setIsModalOpen(true);
      } else {
        setModalStatus('failed');
        setRcaStatus('failed');
        setIsModalOpen(true);
      }
    } catch {
      if (!isMounted.current) return;
      setModalStatus('failed');
      setRcaStatus('failed');
      setIsModalOpen(true);
    } finally {
      if (isMounted.current) {
        setGeneratingId(null);
        generateLoopRef.current = null; // clear so incident-switch logic works correctly
      }
    }
  }, [generatingId, currentIncidentId]);

  // keys you DO NOT want to display
  const hiddenKeys = new Set([
    "sys_id",
    "acknowledged",
    "correlation_group",
    "correlation_rule_group",
    "event_count",
    "flap_count",
    "flap_start_window",
    "is_group_alert",
    "last_update_time_by_event",
    "maintenance",
    "node",
    "sys_mod_count",
    "timestamp",
    "u_reopen_counter",
    "uuid",
    "mappedSeverity",
    "data"
  ]);

  // custom display names
  const labelMap: Record<string, string> = {
    number: ["Incidents"].includes(header) ? "Incident ID" : ["Service Requests"].includes(header) ? "Service Request ID" : ["Problem Tickets"].includes(header) ? "Problem ID" : ["Change Requests"].includes(header) ? "Change Request ID" : "Alert ID",
    severity: "Severity",
    sn_priority_group: "Priority",
    state: "Status",
    assignment_group: "Assignment Group",
    initial_event_time: "Initial Event Time",
    cmdb_ci: "CI",
    sys_created_by: "Created By",
    sys_updated_by: "Updated By",
    message_key: "Message Key",
    businessUnit: "Business Unit",
    kb_url: "KB URL",
    alertId: "Alert ID",
    alertRuleID: "Alert Rule ID",
    dimensions: "Resources Affected",
    dataQuality: "Data Quality",
    dqErrorMessage: "DQ Error Message",
  };

  const formatKey = (key: string) => {
    if (labelMap[key]) {
      return labelMap[key];
    }

    return key
      .replace(/([A-Z])/g, " $1")
      .replace(/_/g, " ")
      .replace(/\b\w/g, (char) => char.toUpperCase())
      .trim();
  };

  const formatValue = (key: string, value: any) => {
    let formattedValue: string | undefined = value
    if (key === 'alertId') {
      const splitItems = value?.split('/')
      const id = splitItems.at(-1)
      formattedValue = id
    } else if (key === 'alertRuleID') {
      const splitItems = value?.split('/')
      const id = splitItems.length > 2 ? splitItems[2] : ''
      formattedValue = id
    } else if (key === 'firedDateTime' || key === 'initial_event_time') {
      formattedValue = new Date(value).toLocaleString();
    } else if (key === 'dimensions') {
      const affectedResources = value?.map((dim: any) => `${dim.name}: ${dim.value}`).join(', ')
      formattedValue = affectedResources
    } else if (key === 'dataQuality') {
      formattedValue = value ? 'True' : 'False';
    } else if (key === 'dqErrorMessage' && typeof value === 'string') {
      // Source description often repeats the same DQ error text twice; collapse it for display only.
      const repeated = value.match(/^(.+?)\s+\1$/s);
      formattedValue = repeated ? repeated[1] : value;
    }
    return formattedValue
  }

  const buildEntries = () => {
    if (!entireRow) return [];

    // filter hidden keys and null dqErrorMessage
    const entries = Object.entries(entireRow).filter(
      ([key, value]) => !hiddenKeys.has(key) && !(key === 'dqErrorMessage' && !value)
    );

    // map display names
    const entriesWithLabel = entries.map(([key, value]) => ({
      key,
      display: formatKey(key),
      value: formatValue(key, value)
    }));

    // sort by display name A-Z
    entriesWithLabel.sort((a, b) => a.display.localeCompare(b.display));

    return entriesWithLabel;
  };

  const finalEntries = useMemo(buildEntries, [entireRow]);

  const severityColorMap: Record<string, { bg: string; color: string }> = {
    CRITICAL: { bg: '#f54b3c', color: '#fff' },
    HIGH: { bg: '#f79227', color: '#fff' },
    MEDIUM: { bg: '#f1aa2c', color: '#fff' },
    LOW: { bg: '#facc32', color: '#fff' },
    Information: { bg: '#37b66e', color: '#fff' },
    MAJOR: { bg: '#f79227', color: '#fff' },
    MINOR: { bg: '#f1aa2c', color: '#fff' },
    WARNING: { bg: '#facc32', color: '#fff' },
    OK: { bg: '#37b66e', color: '#fff' },
    Critical: { bg: '#f54b3c', color: '#fff' },
    Major: { bg: '#f79227', color: '#fff' },
    Minor: { bg: '#f1aa2c', color: '#fff' },
    Warning: { bg: '#facc32', color: '#fff' },
  };

  const priorityColorMap: Record<string, { bg: string; color: string }> = {
    Urgent: { bg: '#f54b3c', color: '#fff' },
    High: { bg: '#f79227', color: '#fff' },
    Moderate: { bg: '#f1aa2c', color: '#fff' },
    Regular: { bg: '#EAAA08', color: '#fff' },
    Low: { bg: '#37b66e', color: '#fff' },
  };

    const getChipStyle = (key: string, value: any) => {
      const valueText = String(value || '').replace(/\u00A0/g, ' ').trim();
      const colorMap = key === 'severity' || key === 'urgency' ? severityColorMap : priorityColorMap;
      const statusText = valueText
        .replace(/^\d+\s*[-–—]\s*/, '')
        .trim();

      const matchedKey = Object.keys(colorMap).find(item =>
        statusText.toLowerCase().includes(item.toLowerCase())
      );

      return matchedKey
        ? colorMap[matchedKey]
        : { bg: '#E0E0E0', color: '#000' };
  };

  const headerTitleMap: Record<string, string> = {
    Incidents: 'Incident Details',
    'Service Requests': 'Service Request Details',
    'Problem Tickets': 'Problem Ticket Details',
    'Change Requests': 'Change Request Details',
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      variant="persistent"
      PaperProps={{
        sx: {
          width: 490,
          maxWidth: 490,
          backgroundColor: overlayColors.background,
          height: "calc(100vh - 76px)",
          top: "76px",
          boxShadow: "-2px 4px 8px rgba(0, 0, 0, 0.1)",
        }
      }}
      ModalProps={{
        BackdropProps: {
          sx: {
            backgroundColor: "rgba(255, 255, 255, 0.5)"
          }
        }
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          backgroundColor: overlayColors.background
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            px: 3,
            py: 2.5,
            borderBottom: `1px solid ${theme.palette.grey[300]}`
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 600, color: overlayColors.title }}>
            {headerTitleMap[header] || header || 'Alert Details'}
          </Typography>
          <Box display="flex" alignItems="center" gap={2}>
            {header === 'Incidents' && (
              <Box
                onClick={handleGenerateRCA}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  cursor: isGeneratingRCA ? 'default' : 'pointer',
                  color: isGeneratingRCA ? buttonColors.disabledText : buttonColors.textColor,
                }}
              >
                <Box
                  sx={{
                    width: '24px',
                    height: '24px',
                    mr: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {isGeneratingRCA ? (
                    <CircularProgress size={20} sx={{ color: buttonColors.disabledText }} />
                  ) : (
                    <GenerateRcaIcon />
                  )}
                </Box>
                <Typography
                  sx={{
                    fontFamily: 'Montserrat',
                    fontWeight: 500,
                    fontSize: '14px',
                    lineHeight: '25px',
                    textTransform: 'uppercase',
                    textAlign: 'right',
                  }}
                >
                  {(() => {
                    if (isGeneratingRCA) return 'GENERATING RCA...';
                    if (rcaStatus === 'success') return 'RE-GENERATE RCA';
                    return 'GENERATE RCA';
                  })()}
                </Typography>
              </Box>
            )}
            <CloseIcon onClick={onClose} sx={{
              color: `${overlayColors.IconColor} !important`,
              cursor: 'pointer'
            }} />
          </Box>
        </Box>

        {/* Body */}
        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            px: 3,
            py: 2,
            backgroundColor: overlayColors.background,
            scrollBehavior: "smooth",
            "&::-webkit-scrollbar": {
              width: "8px"
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: scrollbarColors.background
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: scrollbarColors.thumb,
              borderRadius: "4px",
              "&:hover": {
                backgroundColor: scrollbarColors.thumb
              }
            }
          }}
        >
          {rcaStatus === 'success' && (
            <Accordion
              expanded={rcaExpanded}
              onChange={(_, expanded) => setRcaExpanded(expanded)}
              disableGutters
              elevation={0}
              sx={{
                mb: 2,
                backgroundColor: rcaExpanded ? overlayColors.background : accordionColors.InsightsBg,
                boxShadow: rcaExpanded ? '0px 3px 12px rgba(0, 0, 0, 0.10)' : 'none',
                border: 'none',
                '&:before': { display: 'none' },
                borderRadius: '4px',
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ color: buttonColors.textColor }} />}
                sx={{
                  minHeight: '48px',
                  backgroundColor: rcaExpanded ? overlayColors.background : accordionColors.InsightsBg,
                  color: textColors.main,
                  '& .MuiAccordionSummary-content': { my: 0 }
                }}
              >
                <Typography sx={{ fontWeight: 500, fontSize: '15px', color: textColors.main }}>RCA(Root Cause Analysis)</Typography>
              </AccordionSummary>
              <AccordionDetails sx={{
                pt: 1, pb: 2, px: 2,
                backgroundColor: `${overlayColors.background} !important`,
                border: 'none',
                '&&': { backgroundColor: `${overlayColors.background} !important` }
              }}>
                <Box sx={{ color: '#333333', fontSize: '0.875rem' }}>
                  <MarkdownRenderer text={rcaReportText} />
                </Box>
              </AccordionDetails>
            </Accordion>
          )}

          {finalEntries.map(({ key, display, value }) => (
            <Box key={key} sx={{ display: 'flex', py: 1.2, borderBottom: `1px solid ${theme.palette.grey[200]}` }}>
              <Typography sx={{ fontWeight: 400, fontSize: '14px', color: textColors.main, opacity: 0.7, width: '45%', flexShrink: 0 }}>
                {display}
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  textAlign: "left",
                  wordBreak: 'break-word',
                  width: "55%",
                  maxWidth: "55%",
                  flexDirection: 'row',
                  justifyContent: 'flex-start',
                  cursor: ["number", "alertId", "alertRuleID"].includes(key) ? "pointer" : "default"
                }}
                onClick={() => {
                  if (key === "number") {
                    const type = SNOW_TYPE_MAP[header] ?? "em_alert";

                    window.open(
                      `${snowUrl}/now/sow/record/${type}/${entireRow.sys_id}`,
                      "_blank"
                    );
                  } else if (key === 'alertId') {
                    window.open(`https://portal.azure.com/#resource/${entireRow.alertId}`, "_blank");
                  } else if (key === 'alertRuleID') {
                    window.open(`https://portal.azure.com/#resource/${entireRow.alertRuleID}`, "_blank");
                  }
                }}
              >
                {key === "incident" ? (
                  (() => {
                    return (
                      <Box
                        sx={{
                          px: 1.5,
                          py: 0.4,
                          borderRadius: "4px",
                          fontWeight: 500,
                          fontSize: '14px',
                          color: `${textColors.main} !important`,
                          cursor: entireRow.inc_sys_id ? "pointer" : "none",
                          textDecoration: entireRow.inc_sys_id ? "underline" : "none",
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          height: "22px"
                        }}
                        onClick={entireRow.inc_sys_id ? () => window.open(`${snowUrl}/now/sow/record/incident/${entireRow.inc_sys_id}`, "_blank") : undefined}
                      >
                        {entireRow.incident || '-'}
                      </Box>
                    );
                  })()
                ) : ["priority", "sn_priority_group", "severity", "urgency"].includes(key) && value !== null && value !== undefined && value !== '' ? (
                  <Box
                    sx={{
                      px: 1.5,
                      py: 0.5,
                      borderRadius: "4px",
                      fontSize: "0.7rem",
                      fontWeight: 600,
                      backgroundColor: getChipStyle(key, value).bg,
                      color: getChipStyle(key, value).color,
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      height: "24px",
                      minWidth: "75px"
                    }}
                  >
                    {`${value}`}
                  </Box>
                ) : key === "dqErrorMessage" && value !== null && value !== undefined && value !== '' ? (
                  <Typography
                    sx={{
                      color: inputColors.error,
                      fontWeight: 500,
                      fontSize: '0.875rem',
                      backgroundColor: overlayColors.background,
                      borderRadius: '4px',
                      px: 1,
                      py: 0.5,
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                    }}
                  >
                    {`${value}`}
                  </Typography>
                ) : (
                  <Typography
                    sx={{
                      fontWeight: 500,
                      fontSize: '14px',
                      color: ["number", "alertId", "alertRuleID"].includes(key) ? buttonColors.textColor : textColors.main,
                      textDecoration: ["number", "alertId", "alertRuleID"].includes(key) && value !== null && value !== undefined && value !== '' ? "underline" : "none"
                    }}
                  >
                    {value === null || value === undefined || value === '' ? '-' : `${value}`}
                  </Typography>
                )}

                {["number", "alertId", "alertRuleID"].includes(key) && value !== null && value !== undefined && value !== '' && (
                  <OpenInNewIcon
                    sx={{
                      fontSize: 16,
                      color: buttonColors.textColor
                    }}
                  />
                )}
              </Box>
            </Box>
          ))}
        </Box>

        {/* Footer */}
        <Box
          sx={{
            px: 3,
            py: 1.5,
            borderTop: `1px solid ${theme.palette.grey[300]}`,
            display: "flex",
            justifyContent: "flex-end",
            gap: 2
          }}
        >
          <Button
            variant="outlined"
            color="secondary"
            onClick={onClose}
            sx={{
              textTransform: "uppercase",
              px: 4,
              py: 1.5,
              fontWeight: 600
            }}
          >
            CLOSE
          </Button>
        </Box>
      </Box>
      <RcaStatusModal
        open={isModalOpen}
        onClose={() => { setIsModalOpen(false); setModalStatus(null); }}
        status={modalStatus}
        incidentId={entireRow?.incident || entireRow?.number || 'Unknown'}
        onReviewClick={() => {
          setIsModalOpen(false);
          // If the drawer was closed, re-open it first
          if (!open && onOpen) {
            onOpen();
          }
          setRcaExpanded(true);
        }}
      />
    </Drawer>
  );
};

export default DetailedAlerts;